import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import "../styles/people-management.css";
import { getPeoplePaginated, deletePerson } from "../services/peopleService";
import { useAppContext } from "../context/AppContext";
import ConfirmModal from "../components/ui/ConfirmModal";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";

const PeopleManagementPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const lastLoadKeyRef = useRef("");
  const { setActiveSection } = useAppContext();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    id: null,
    name: "",
    isBulk: false,
    count: 0,
  });
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");
  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("search") || "",
  );
  const [debouncedSearch, setDebouncedSearch] = useState(() =>
    (searchParams.get("search") || "").trim(),
  );
  const [page, setPage] = useState(() => {
    const p = Number(searchParams.get("page") || 1);
    return Number.isInteger(p) && p > 0 ? p : 1;
  });
  const [limit, setLimit] = useState(() => {
    const l = Number(searchParams.get("limit") || 10);
    return Number.isInteger(l) && l > 0 ? l : 10;
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getPeoplePaginated({
        page,
        limit,
        search: debouncedSearch,
      });
      setList(Array.isArray(result?.data) ? result.data : []);
      setPagination(
        result?.meta || {
          total: 0,
          page: Number(page) || 1,
          limit: Number(limit) || 10,
          totalPages: 1,
        },
      );
      setSelectedIds([]);
    } catch (e) {
      toast.error(e?.message || "Failed to load people");
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch((searchInput || "").trim());
      setPage(1);
    }, 450);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const pageStr = String(page);
    const limitStr = String(limit);
    const searchStr = String(searchInput || "").trim();

    if (params.get("page") !== pageStr) params.set("page", pageStr);
    if (params.get("limit") !== limitStr) params.set("limit", limitStr);
    if (searchStr) params.set("search", searchStr);
    else params.delete("search");

    const current = searchParams.toString();
    const next = params.toString();
    if (current !== next) {
      setSearchParams(params, { replace: true });
    }
  }, [page, limit, searchInput, searchParams, setSearchParams]);

  useEffect(() => {
    const key = `${page}:${limit}:${debouncedSearch}`;
    if (lastLoadKeyRef.current === key) return;
    lastLoadKeyRef.current = key;
    reload();
  }, [page, limit, debouncedSearch, reload]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".actions-menu-wrapper")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close dropdown on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") setActiveDropdown(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const requestDelete = (id, name) => {
    setConfirmDelete({ open: true, id, name: name || "this person", isBulk: false, count: 0 });
  };

  const openBulkDeleteConfirm = () => {
    if (selectedIds.length === 0) return;
    setConfirmDelete({
      open: true,
      id: null,
      name: "",
      isBulk: true,
      count: selectedIds.length,
    });
  };

  const onDeleteConfirmed = async () => {
    if (confirmDelete.isBulk) {
      await confirmBulkDelete();
      return;
    }
    const id = confirmDelete.id;
    const name = confirmDelete.name;
    if (!id) {
      setConfirmDelete({ open: false, id: null, name: "", isBulk: false, count: 0 });
      return;
    }
    try {
      setConfirmDelete((prev) => ({ ...prev, open: false }));
      setList((prev) =>
        Array.isArray(prev) ? prev.filter((p) => p.people_id !== id) : prev,
      );
      await deletePerson(id);
      toast.success(`Person "${name}" has been deleted successfully!`);
      await reload();
    } catch (e) {
      toast.error(e?.message || "Failed to delete person");
      await reload();
    } finally {
      setConfirmDelete({ open: false, id: null, name: "", isBulk: false, count: 0 });
    }
  };

  const confirmBulkDelete = async () => {
    const idsToDelete = [...selectedIds];
    try {
      setBulkDeleting(true);
      setConfirmDelete({ open: false, id: null, name: "", isBulk: false, count: 0 });
      for (const id of idsToDelete) {
        await deletePerson(id);
      }
      toast.success(
        `${idsToDelete.length} ${
          idsToDelete.length > 1 ? "people" : "person"
        } deleted successfully!`,
      );
      setSelectedIds([]);
      await reload();
    } catch (e) {
      toast.error(e?.message || "Failed to delete some people");
      await reload();
    } finally {
      setBulkDeleting(false);
    }
  };

  // Multiple selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.length === list.length && list.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(list.map((p) => p.people_id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ?
        prev.filter((selectedId) => selectedId !== id)
      : [...prev, id],
    );
  };

  const clearSelection = () => {
    setSelectedIds([]);
  };

  const goView = (id) => {
    try {
      localStorage.setItem("activePersonId", String(id));
    } catch {}
    setActiveSection("people-view", { personId: id });
  };

  const goEdit = (id) => {
    try {
      localStorage.setItem("activePersonId", String(id));
    } catch {}
    setActiveSection("people-edit", { personId: id });
  };

  const fullName = (p) =>
    [p?.people_name, p?.people_surname].filter(Boolean).join(" ") || "-";

  const totalPages = Math.max(1, Number(pagination.totalPages) || 1);
  const currentPage = Math.min(
    Math.max(1, Number(pagination.page) || page),
    totalPages,
  );
  const totalRows = Number(pagination.total) || 0;
  const pageSize = Number(pagination.limit) || limit;
  const showingFrom = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingTo =
    totalRows === 0 ? 0 : Math.min(currentPage * pageSize, totalRows);

  const visiblePageItems = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 2) {
      return [1, 2, 3, "ellipsis", totalPages];
    }

    if (currentPage === 3) {
      return [1, 2, 3, 4, "ellipsis", totalPages];
    }

    if (currentPage === totalPages - 2) {
      return [
        1,
        "ellipsis",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    if (currentPage >= totalPages - 1) {
      return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
    }

    return [
      1,
      "ellipsis",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "ellipsis",
      totalPages,
    ];
  }, [totalPages, currentPage]);

  const limitOptions = useMemo(() => {
    const base = [5, 10, 20, 50];
    const current = Number(limit);
    const all =
      Number.isInteger(current) && current > 0 ? [...base, current] : base;
    return Array.from(new Set(all)).sort((a, b) => a - b);
  }, [limit]);

  return (
    <div className="page-container people-page">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">People</h2>
            <p className="page-subtitle">
              Browse people. Use actions to manage.
            </p>
          </div>
          <div className="roles-toolbar">
            <div className="roles-search">
              <input
                className="search-input"
                type="text"
                placeholder="Search by name, email, phone"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button
                className="secondary-btn sm"
                onClick={() => setSearchInput("")}
                disabled={loading}
              >
                Clear
              </button>
            </div>
            <button
              className="primary-btn"
              onClick={() => setActiveSection("people-create")}
            >
              + Add Person
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="people-pagination-top">
          <label htmlFor="people-limit" className="people-pagination-label">
            Rows per page:
          </label>
          <select
            id="people-limit"
            value={limit}
            onChange={(e) => {
              const next = Number(e.target.value) || 10;
              setLimit(next);
              setPage(1);
            }}
          >
            {limitOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        {selectedIds.length > 0 && (
          <div
            style={{
              marginBottom: "1rem",
              padding: "1rem 1.25rem",
              background: "var(--accent-tint-50)",
              borderRadius: "0.75rem",
              border: "1px solid var(--accent-tint-200)",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "0.75rem",
              }}
            >
              <span style={{ fontWeight: "600", color: "var(--accent-ink-800)", fontSize: "0.9375rem" }}>
                {selectedIds.length} {selectedIds.length > 1 ? "people" : "person"} selected
              </span>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <button
                  className="danger-btn sm"
                  onClick={openBulkDeleteConfirm}
                  disabled={bulkDeleting}
                >
                  {bulkDeleting ? "Deleting..." : "Delete Selected"}
                </button>
                <button className="secondary-btn sm" onClick={clearSelection}>
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}
        {loading ?
          <div className="no-results">Loading people...</div>
        : <div className="roles-table">
            <div className="roles-table-header">
              <div className="cell checkbox">
                <input
                  type="checkbox"
                  checked={
                    selectedIds.length > 0 &&
                    selectedIds.length === list.length &&
                    list.length > 0
                  }
                  onChange={toggleSelectAll}
                />
              </div>
              <div className="cell">Full Name</div>
              <div className="cell">Is Manager</div>
              <div className="cell">Email</div>
              <div className="cell">Phone</div>
              <div className="cell actions">Actions</div>
            </div>

            {!list || list.length === 0 ?
              <div className="no-results">No people found</div>
            : list.map((p) => (
                <div
                  key={p.people_id}
                  className={`roles-table-row ${selectedIds.includes(p.people_id) ? "selected" : ""}`}
                >
                  <div className="cell checkbox">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(p.people_id)}
                      onChange={() => toggleSelect(p.people_id)}
                    />
                  </div>
                  <div className="cell">{fullName(p)}</div>
                  <div className="cell">{p?.is_manager ? "Yes" : "No"}</div>
                  <div className="cell">{p?.people_email || "-"}</div>
                  <div className="cell">{p?.people_phone || "-"}</div>
                  <div className="cell actions">
                    <div
                      className="actions-menu-wrapper"
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <button
                        className="action-menu-btn"
                        aria-haspopup="true"
                        aria-expanded={activeDropdown === p.people_id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeDropdown === p.people_id) {
                            setActiveDropdown(null);
                          } else {
                            setDropdownDirection(getActionDropdownDirection(e.currentTarget));
                            setActiveDropdown(p.people_id);
                          }
                        }}
                      >
                        Actions ▼
                      </button>
                      {activeDropdown === p.people_id && (
                        <div
                          className={`action-dropdown-menu${
                            dropdownDirection === "up" ? " dropdown-up" : ""
                          }`}
                          role="menu"
                        >
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              goView(p.people_id);
                            }}
                          >
                            View
                          </button>
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              goEdit(p.people_id);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="action-menu-item danger"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              requestDelete(p.people_id, fullName(p));
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            }
          </div>
        }

        <div className="people-pagination-bottom">
          <div className="people-pagination-summary">
            Showing {showingFrom} to {showingTo} of {totalRows} entries
          </div>
          <div className="people-pagination-actions">
            <button
              className="secondary-btn sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || loading}
            >
              Previous
            </button>
            {visiblePageItems.map((item, index) => {
              if (item === "ellipsis") {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    style={{ padding: "0 6px", color: "var(--gray-500)" }}
                    aria-hidden="true"
                  >
                    …
                  </span>
                );
              }
              const p = Number(item);
              return (
                <button
                  key={p}
                  className={
                    p === currentPage ? "primary-btn sm" : "secondary-btn sm"
                  }
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              );
            })}
            <button
              className="secondary-btn sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages || loading}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={confirmDelete.open}
        title={confirmDelete.isBulk ? "Delete People" : "Delete Person"}
        message={
          confirmDelete.isBulk
            ? `Are you sure you want to delete ${confirmDelete.count} ${
                confirmDelete.count > 1 ? "people" : "person"
              }? This action cannot be undone.`
            : `Are you sure you want to delete "${confirmDelete.name}"? This action cannot be undone.`
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={onDeleteConfirmed}
        onCancel={() =>
          setConfirmDelete({ open: false, id: null, name: "", isBulk: false, count: 0 })
        }
        isDanger={true}
      />
    </div>
  );
};

export default PeopleManagementPage;

